import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useInitDraftSitrepV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import CreateDraftReportModal, {
  OPORD_TEMPLATE_ID,
  type CreateDraftReportSelection,
} from 'components/Reports/CreateDraftReportModal';
import {
  SitrepViewDataProps,
  SitrepViewTabs,
} from 'components/Reports/reports.types';
import CurrentReportView from 'components/Reports/CurrentReportView';
import DraftReportView from 'components/Reports/DraftReportView';
import HistoricalReportView from 'components/Reports/HistoricalReportView';
import ReportChainList from 'components/Reports/ReportChainList';
import { registerReportsTourApi } from 'components/Reports/reportsTour/reportsTourBridge';
import { buildDraftSitrepPayload } from 'components/Reports/utils/buildDraftSitrepPayload';
import { partitionPublishedSitreps } from 'components/Reports/utils/partitionPublishedSitreps';
import { buildReportChains } from 'components/Reports/utils/reportChains';
import { Incident } from 'components/IAP/hooks/incidentHooks';
import { AreaOfResponsibility } from 'types/aor';
import { useReportVersioningFlag } from 'utils/featureFlags';

const getAorIdFromIncident = (
  incident: Incident,
  aors: AreaOfResponsibility[]
) => {
  const incidentAors = aors.filter((aor) =>
    incident.areas_of_responsibility.includes(aor.id)
  );

  return incidentAors[0]?.id ?? null;
};

type ReportSitrepViewProps = {
  scopeName: string;
  publishedSitreps: SitrepViewDataProps;
  draftSitreps: SitrepViewDataProps;
  pendingReviews: SitrepViewDataProps;
  aors: AreaOfResponsibility[];
  aorId?: string;
  incident?: Incident;
};

const VISIBLE_SITREP_TABS = [
  SitrepViewTabs.CurrentSitrep,
  SitrepViewTabs.Historical,
  SitrepViewTabs.Drafts,
] as const;

const getTabDisplayName = (tab: SitrepViewTabs) => {
  switch (tab) {
    case SitrepViewTabs.CurrentSitrep:
      return 'Current';
    case SitrepViewTabs.Historical:
      return 'Historical';
    case SitrepViewTabs.Drafts:
      return 'Drafts';
    default:
      return '';
  }
};

const resolveSitrepTab = (raw: string | null): SitrepViewTabs => {
  if (raw === SitrepViewTabs.Drafts || raw === SitrepViewTabs.Reviews) {
    return SitrepViewTabs.Drafts;
  }
  if (raw === SitrepViewTabs.Historical) {
    return SitrepViewTabs.Historical;
  }
  return SitrepViewTabs.CurrentSitrep;
};

const ReportsSitrepView: React.FC<ReportSitrepViewProps> = ({
  scopeName,
  publishedSitreps,
  draftSitreps,
  pendingReviews,
  aors,
  aorId,
  incident,
}) => {
  const reportVersioning = useReportVersioningFlag();
  const initDraftMutation = useInitDraftSitrepV2();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [preferOpord, setPreferOpord] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = useMemo(
    () => resolveSitrepTab(searchParams.get('sitrepTab')),
    [searchParams]
  );
  const setCurrentTab = useCallback(
    (newTab: SitrepViewTabs) => {
      setSearchParams(
        (searchParams) => {
          searchParams.set('sitrepTab', newTab);
          return searchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { latestSitreps, historicalSitreps } = useMemo(
    () => partitionPublishedSitreps(publishedSitreps.data),
    [publishedSitreps]
  );
  const reportChains = useMemo(
    () =>
      buildReportChains([
        ...publishedSitreps.data,
        ...draftSitreps.data,
        ...pendingReviews.data,
      ]),
    [publishedSitreps.data, draftSitreps.data, pendingReviews.data]
  );

  const derivedAorId = useMemo(() => {
    if (aorId) {
      return aorId;
    } else if (incident) {
      return getAorIdFromIncident(incident, aors);
    }

    console.error(
      'No AOR ID found matching the provided incident. Unable to create draft SITREP with correct AOR association.'
    );
    return undefined;
  }, [incident, aors, aorId]);

  const buildPayload = (selection: CreateDraftReportSelection) =>
    buildDraftSitrepPayload({
      scopeName,
      aorId: derivedAorId,
      incidentId: incident?.id,
      templateId: selection.templateId,
      skipDefaultSections: selection.skipDefaultSections,
    });

  // The tour API is registered once; refs keep it pointed at current scope.
  const buildPayloadRef = useRef(buildPayload);
  buildPayloadRef.current = buildPayload;
  const canCreateDraft = Boolean(derivedAorId);
  const hasInProgressDraft =
    draftSitreps.data.length > 0 || pendingReviews.data.length > 0;
  const canCreateDraftRef = useRef(canCreateDraft);
  canCreateDraftRef.current = canCreateDraft;
  const hasInProgressDraftRef = useRef(hasInProgressDraft);
  hasInProgressDraftRef.current = hasInProgressDraft;
  const mutateAsyncRef = useRef(initDraftMutation.mutateAsync);
  mutateAsyncRef.current = initDraftMutation.mutateAsync;
  const draftsRef = useRef(draftSitreps.data);
  draftsRef.current = draftSitreps.data;
  const pendingRef = useRef(pendingReviews.data);
  pendingRef.current = pendingReviews.data;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    return registerReportsTourApi({
      openCreateDraftModal: () => {
        setPreferOpord(true);
        setShowCreateModal(true);
      },
      closeCreateDraftModal: () => {
        setShowCreateModal(false);
        setPreferOpord(false);
      },
      openExistingDraft: () => {
        const pendingIds = new Set(
          pendingRef.current.map((sitrep) => sitrep.id)
        );
        const editable = draftsRef.current.find(
          (sitrep) => !pendingIds.has(sitrep.id)
        );
        const draft = editable ?? draftsRef.current[0] ?? pendingRef.current[0];
        setShowCreateModal(false);
        setPreferOpord(false);
        if (!draft) return false;
        setSearchParamsRef.current(
          (params) => {
            params.set('sitrepTab', SitrepViewTabs.Drafts);
            params.set('openReport', draft.id);
            return params;
          },
          { replace: true }
        );
        return true;
      },
      confirmCreateDraft: async () => {
        if (!canCreateDraftRef.current || hasInProgressDraftRef.current) {
          return;
        }
        try {
          await mutateAsyncRef.current(
            buildPayloadRef.current({
              templateId: OPORD_TEMPLATE_ID,
              skipDefaultSections: false,
            })
          );
        } catch {
          // Without a card the remaining steps skip themselves; say why.
          toast.error('Failed to create the walkthrough draft report.');
        } finally {
          setShowCreateModal(false);
          setPreferOpord(false);
        }
      },
    });
  }, []);

  const handleCreateDraft = (selection: CreateDraftReportSelection) => {
    initDraftMutation.mutate(buildPayload(selection), {
      onSuccess: () => setShowCreateModal(false),
      onError: (error: { response?: { data?: { error?: unknown } } }) => {
        const message = error.response?.data?.error;
        toast.error(
          typeof message === 'string'
            ? message
            : 'Failed to create the draft report.'
        );
      },
    });
  };

  const getTabDisplay = useMemo(() => {
    switch (currentTab) {
      case SitrepViewTabs.CurrentSitrep:
        return (
          <CurrentReportView
            scopeName={scopeName}
            latestSitreps={latestSitreps}
            isLoading={publishedSitreps.isLoading}
            isError={publishedSitreps.isError}
            derivedAorId={derivedAorId}
            hasInProgressDraft={hasInProgressDraft}
          />
        );
      case SitrepViewTabs.Historical:
        return (
          <HistoricalReportView
            historicalSitreps={historicalSitreps}
            isLoading={publishedSitreps.isLoading}
            isError={publishedSitreps.isError}
            scopeName={scopeName}
          />
        );
      case SitrepViewTabs.Drafts:
        return (
          <DraftReportView
            draftSitreps={draftSitreps}
            pendingReviews={pendingReviews}
            scopeName={scopeName}
            derivedAorId={derivedAorId}
          />
        );
      default:
        return null;
    }
  }, [
    currentTab,
    scopeName,
    latestSitreps,
    historicalSitreps,
    draftSitreps,
    pendingReviews,
    publishedSitreps.isLoading,
    publishedSitreps.isError,
    derivedAorId,
    hasInProgressDraft,
  ]);

  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-1 tw:flex-col">
      {reportVersioning ? null : (
        <div
          className="tw:mb-3 tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-2 tw:bg-background tw:pr-4"
          data-tour="reports-tabs"
        >
          <Tabs
            value={currentTab}
            onValueChange={(value) => setCurrentTab(value as SitrepViewTabs)}
            className="reports-sitrep-tab-selector"
          >
            <TabsList>
              {VISIBLE_SITREP_TABS.map((tab) => (
                <TabsTrigger key={tab} value={tab}>
                  {getTabDisplayName(tab)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {canCreateDraft ? (
            hasInProgressDraft ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className="tw:inline-flex">
                        <Button
                          type="button"
                          data-tour="reports-create-draft"
                          disabled
                          className="tw:pointer-events-none"
                        >
                          <Plus />
                          Create Draft Report
                        </Button>
                      </span>
                    }
                  />
                  <TooltipContent className="dark">
                    There is already a working draft report for {scopeName}.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                type="button"
                data-tour="reports-create-draft"
                disabled={initDraftMutation.isPending}
                onClick={() => setShowCreateModal(true)}
              >
                <Plus />
                Create Draft Report
              </Button>
            )
          ) : null}
        </div>
      )}
      <ScrollArea
        className="tw:mr-0 tw:min-h-0 tw:w-full tw:flex-1"
        viewportClassName="tw:pr-4"
        scrollbarClassName="tw:absolute tw:top-0 tw:right-0 tw:z-10"
      >
        {reportVersioning ? (
          <ReportChainList
            scopeName={scopeName}
            chains={reportChains}
            isLoading={
              publishedSitreps.isLoading ||
              draftSitreps.isLoading ||
              pendingReviews.isLoading
            }
            isError={
              publishedSitreps.isError ||
              draftSitreps.isError ||
              pendingReviews.isError
            }
            derivedAorId={derivedAorId}
            creating={initDraftMutation.isPending}
            onCreate={() => setShowCreateModal(true)}
          />
        ) : (
          getTabDisplay
        )}
      </ScrollArea>
      <CreateDraftReportModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setPreferOpord(false);
        }}
        onConfirm={handleCreateDraft}
        isSubmitting={initDraftMutation.isPending}
        preferredTemplateId={preferOpord ? OPORD_TEMPLATE_ID : undefined}
      />
    </div>
  );
};

export default ReportsSitrepView;
